import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faCode } from '@fortawesome/free-solid-svg-icons/faCode';
import { faCube } from '@fortawesome/free-solid-svg-icons/faCube';
import { faPlay } from '@fortawesome/free-solid-svg-icons/faPlay';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons/faWandMagicSparkles';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { deviceSizes } from '@/shared/config';
import { Button } from '@/widgets/footer';
import { scrollToNextSection, scrollToVideoSection } from './runtime';

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 96px 0 44px;
  background: var(--gradient-hero-canvas);
  color: var(--text-title);
`;

const HeroContent = styled.div`
  position: relative;
  margin: 0 auto;
  width: min(1280px, 94%);
  z-index: 3;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 56px;

  @media screen and (max-width: ${deviceSizes.lg}) {
    flex-direction: column;
    text-align: center;
    gap: 44px;
  }
`;

const TextColumn = styled.div`
  flex: 1 1 480px;
  min-width: 0;
  text-align: left;

  @media screen and (max-width: ${deviceSizes.lg}) {
    text-align: center;
  }
`;

const VideoColumn = styled.div`
  flex: 1 1 460px;
  min-width: 0;
  width: 100%;
`;

const IntroVideo = styled.video`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-md);
  background: var(--surface-dark);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--hairline);
`;

const VideoCaption = styled.p`
  margin-top: 10px;
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-align: center;
`;

const SloganText = styled.h1`
  margin: 0;
  color: var(--text-title);
  font-size: clamp(32px, 4.2vw, 64px);
  font-weight: var(--weight-extrabold);
  line-height: var(--leading-tight);
`;

const Subtitle = styled.p`
  color: var(--text-title);
  font-family: var(--font-sans);
  font-size: clamp(13px, 1.35vw, 20px);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-snug);
  margin: 18px 0 0;
  white-space: normal;

  /* Below this, even the smallest clamp size can't fit the line without
     overflowing the viewport — let it wrap rather than clip/scroll. */
  @media screen and (max-width: 360px) {
    white-space: normal;
  }
`;

const BodyText = styled.p`
  max-width: 520px;
  margin: 14px 0 0;
  color: var(--text-muted);
  font-family: var(--font-sans);
  font-size: var(--text-md);
  line-height: var(--leading-relaxed);

  @media screen and (max-width: ${deviceSizes.lg}) {
    margin-inline: auto;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 30px;

  @media screen and (max-width: ${deviceSizes.lg}) {
    justify-content: center;
  }
`;

const WatchDemoButton = styled.button`
  &:active {
    background: var(--surface-hover);
  }
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-sans);
  font-weight: var(--weight-bold);
  font-size: var(--text-md);
  color: var(--text-title);
  background: var(--surface-card);
  border: 2px solid var(--border-control);
  border-radius: var(--radius-pill);
  padding: 15px 26px;
  min-height: 52px;
  line-height: 1;
  cursor: pointer;
  transition:
    background var(--duration-fast) ease,
    border-color var(--duration-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: var(--surface-hover);
      border-color: var(--border-control);
    }
  }
`;

const FeatureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(5px, 0.9vw, 16px);
  margin-top: 40px;

  @media screen and (max-width: ${deviceSizes.lg}) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 22px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.45vw, 8px);

  @media screen and (max-width: ${deviceSizes.lg}) {
    gap: 10px;
  }
`;

const FeatureIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(18px, 1.8vw, 30px);
  height: clamp(18px, 1.8vw, 30px);
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--brand-soft);
  color: var(--brand-strong);
  font-size: clamp(8px, 0.8vw, 12px);

  @media screen and (max-width: ${deviceSizes.lg}) {
    width: 34px;
    height: 34px;
    font-size: 14px;
  }
`;

const FeatureLabel = styled.span`
  color: var(--text-title);
  font-family: var(--font-sans);
  font-size: clamp(9px, 0.8vw, 12.5px);
  font-weight: var(--weight-bold);
  white-space: nowrap;

  @media screen and (max-width: ${deviceSizes.lg}) {
    font-size: var(--text-sm);
  }
`;

const FeatureDivider = styled.span`
  width: 1px;
  height: 26px;
  background: var(--hairline);
  flex-shrink: 0;

  @media screen and (max-width: ${deviceSizes.lg}) {
    display: none;
  }
`;

const ScrollHolder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 56px;
  position: relative;
  z-index: 4;
`;

const ScrollButton = styled.button`
  &:active {
    opacity: 0.8;
  }
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: var(--scroll-button);
  height: var(--scroll-button);
  border: none;
  border-radius: var(--radius-pill);
  background: var(--surface-card);
  color: var(--text-title);
  font-size: 18px;
  border: 1px solid var(--border-control);
  cursor: pointer;
`;

interface FeatureConfig {
  id: string;
  messageId: string;
  icon: IconDefinition;
}

const FEATURES: FeatureConfig[] = [
  { id: 'ai-tools', messageId: 'TopBanner.BriefIntroduction.0', icon: faWandMagicSparkles },
  { id: 'code-design', messageId: 'TopBanner.BriefIntroduction.1', icon: faCode },
  { id: 'ai-3d-projects', messageId: 'TopBanner.BriefIntroduction.2', icon: faCube },
];

interface HeroProps {
  createHref: string;
}

export function Hero({ createHref }: HeroProps) {
  const intl = useIntl();
  const heroTitle = intl.formatMessage({ id: 'TopBanner.slogan' });
  const demoVideoUrl = 'https://videos.creaticode.com/CreatiCode.mp4';
  const demoVideoPosterUrl = '/images/creaticode-mars-poster.jpg';

  return (
    <HeroSection id="hero-section">
      <HeroContent>
        <TextColumn>
          <SloganText>{heroTitle}</SloganText>

          <Subtitle>
            <FormattedMessage id="TopBanner.subtitle" />
          </Subtitle>

          <BodyText>
            <FormattedMessage id="TopBanner.whatBody" />
          </BodyText>

          <CtaRow>
            <Button href={createHref} size="lg" variant="primary">
              <FormattedMessage id="Hero.startCreatingFree" />
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <WatchDemoButton onClick={scrollToVideoSection} type="button">
              <FontAwesomeIcon icon={faPlay} />
              <FormattedMessage id="Hero.watchDemo" />
            </WatchDemoButton>
          </CtaRow>

          <FeatureRow>
            {FEATURES.map((feature, i) => (
              <FeatureItem key={feature.id}>
                {i > 0 ? <FeatureDivider aria-hidden="true" /> : null}
                <FeatureIcon>
                  <FontAwesomeIcon icon={feature.icon} />
                </FeatureIcon>
                <FeatureLabel>
                  <FormattedMessage id={feature.messageId} />
                </FeatureLabel>
              </FeatureItem>
            ))}
          </FeatureRow>
        </TextColumn>

        <VideoColumn>
          <IntroVideo
            aria-label={intl.formatMessage({ id: 'Hero.watchDemo' })}
            controls
            muted
            playsInline
            poster={demoVideoPosterUrl}
            preload="none"
            src={demoVideoUrl}
          />
          <VideoCaption>
            <FormattedMessage id="ExampleProjects.slides.rover.title" />
          </VideoCaption>
        </VideoColumn>
      </HeroContent>

      <ScrollHolder>
        <ScrollButton aria-label="Scroll to next section" onClick={scrollToNextSection} type="button">
          <FontAwesomeIcon icon={faArrowDown} />
        </ScrollButton>
      </ScrollHolder>
    </HeroSection>
  );
}
